import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  reportInvoices,
  reportLowStock,
  reportPurchases,
  reportSales,
} from '../services/reportsService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import StatCard from '../components/ui/StatCard';
import Badge from '../components/ui/Badge';

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthAgoISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

export default function ReportsPage() {
  const { ruc } = useAuth();
  const [desde, setDesde] = useState(monthAgoISO());
  const [hasta, setHasta] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const run = useCallback(async () => {
    if (!ruc) return;
    if (!desde && !hasta) {
      setError('Selecciona al menos una fecha de inicio o fin.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Fechas como strings ISO YYYY-MM-DD (mismo formato que fechaEmision)
      const [ventas, compras, stock, comps] = await Promise.all([
        reportSales({ ruc, desde, hasta }),
        reportPurchases({ ruc, desde, hasta }),
        reportLowStock({ ruc }),
        reportInvoices({ ruc, desde, hasta }),
      ]);
      setData({ ventas, compras, stock, comps });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc, desde, hasta]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Reportes</h1>
          <p className="muted">
            Consultas bajo demanda con rango de fechas · sin descargar todo el histórico
          </p>
        </div>
      </header>

      <div className="toolbar">
        <label className="checkbox">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="checkbox">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button type="button" className="btn btn-primary" onClick={run} disabled={loading}>
          {loading ? 'Consultando…' : 'Ejecutar reporte'}
        </button>
      </div>

      {error ? <ErrorState message={error} onRetry={run} /> : null}
      {loading && !data ? <LoadingState label="Generando reporte…" /> : null}

      {!data && !loading ? (
        <div className="panel">
          <p className="muted">
            Elige un periodo y pulsa <strong>Ejecutar reporte</strong>. Ejemplo: últimos 30 días.
          </p>
        </div>
      ) : null}

      {data ? (
        <>
          <section className="stat-grid">
            <StatCard label="Ventas" value={formatMoney(data.ventas.total)} hint={`${data.ventas.count} pedidos · ${data.ventas.pagados} pagados`} />
            <StatCard label="Comprobantes" value={formatMoney(data.comps.total)} hint={`${data.comps.count} docs`} />
            <StatCard label="Compras" value={formatMoney(data.compras.total)} hint={`${data.compras.count} registros`} />
            <StatCard label="Stock bajo" value={data.stock.count} tone={data.stock.count ? 'warn' : 'default'} hint="productos en mínimo" />
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2>Ventas (pedidos)</h2>
              <Link to="/admin/ventas">Módulo ventas</Link>
            </header>
            {data.ventas.rows.length === 0 ? (
              <EmptyState title="Sin ventas en el periodo" />
            ) : (
              <ul className="simple-list">
                {data.ventas.rows.slice(0, 10).map((r) => (
                  <li key={r.id}>
                    <div>
                      <strong>#{r.pedido}</strong>
                      <div className="muted">
                        {formatDate(r.fecha)} · {r.clienteNombre || r.cliente || '—'}
                      </div>
                    </div>
                    <div className="row-end">
                      <span>{formatMoney(r.total)}</span>
                      <Badge tone={r.cancelado ? 'muted' : r.status === 'pagado' ? 'ok' : 'neutral'}>
                        {r.cancelado ? 'cancelado' : r.status || '—'}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2>Comprobantes</h2>
              <Link to="/admin/comprobantes">Módulo comprobantes</Link>
            </header>
            {data.comps.rows.length === 0 ? (
              <EmptyState title="Sin comprobantes en el periodo" />
            ) : (
              <ul className="simple-list">
                {data.comps.rows.slice(0, 10).map((r) => (
                  <li key={`${r.tipo}-${r.id}`}>
                    <div>
                      <strong>
                        {r.serie}-{r.correlativo}
                      </strong>
                      <div className="muted">
                        {r.tipo} · {formatDate(r.fechaEmision)} · {r.rznSocial || '—'}
                      </div>
                    </div>
                    <span>{formatMoney(r.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2>Compras</h2>
              <Link to="/admin/compras">Módulo compras</Link>
            </header>
            {data.compras.rows.length === 0 ? (
              <EmptyState title="Sin compras en el periodo" />
            ) : (
              <ul className="simple-list">
                {data.compras.rows.slice(0, 10).map((r) => (
                  <li key={r.id}>
                    <div>
                      <strong>
                        {r.serie}-{r.correlativo}
                      </strong>
                      <div className="muted">
                        {formatDate(r.fechaEmision)} · {r.proveedorRazonSocial || '—'}
                      </div>
                    </div>
                    <span>{formatMoney(r.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2>Stock bajo</h2>
              <Link to="/admin/inventario">Inventario</Link>
            </header>
            {data.stock.rows.length === 0 ? (
              <EmptyState title="Sin alertas de stock mínimo" />
            ) : (
              <ul className="simple-list">
                {data.stock.rows.map((p) => (
                  <li key={p.uid}>
                    <div>
                      <strong>{p.name}</strong>
                      <div className="muted">Mín. {p.stockMinimo || 0}</div>
                    </div>
                    <Badge tone="warn">Stock {p.stock ?? 0}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
